import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const freeCallAnalysisArticle: BlogArticle = {
  key: BlogArticleKey.FREE_CALL_ANALYSIS,
  slug: BlogSlugs.FREE_CALL_ANALYSIS,
  categoryId: "01",
  date: "2026-08-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Analyser ses appels de vente gratuitement",
      excerpt:
        "Plusieurs outils enregistrent et transcrivent sans rien te faire payer, et ils sont très bons. Voici lesquels, ce que couvre réellement leur offre gratuite, et le plafond qu'aucun d'eux ne dépasse.",
      description:
        "Quels outils permettent d'analyser ses appels de vente gratuitement, et jusqu'où va vraiment leur offre gratuite ? Comparatif des paliers gratuits de Fathom, tl;dv et Fireflies, et la limite commune à tous.",
      ctaTitle: "Teste ce qui vient après la transcription",
      ctaText:
        "Meidly t'offre des crédits pour analyser tes propres appels et voir ce que ça donne quand la donnée nourrit ton ciblage. Sans carte bancaire.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Oui, on peut analyser ses appels sans payer. Et non, ce n'est pas une version bridée qui te pousse à sortir la carte au bout de trois réunions.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le marché des notetakers s'est tellement rempli que la gratuité est devenue une arme commerciale. Plusieurs outils offrent aujourd'hui un palier gratuit réellement utilisable au quotidien, pas une démonstration déguisée.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Encore faut-il savoir ce que chacun couvre, et surtout où s'arrête le gratuit dans tous les cas.",
        },
        { type: ArticleBlockType.H2, text: "Ce que gratuit veut dire ici" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Trois choses différentes se cachent derrière le mot, et c'est ce qui rend les comparatifs illisibles :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Gratuit sur le volume** : nombre de réunions et durée de transcription illimités. C'est le vrai gratuit.",
            "**Gratuit avec des crédits IA** : tu enregistres tant que tu veux, mais les résumés et les questions posées à l'IA sont comptés.",
            "**Gratuit à l'essai** : quelques jours ou quelques heures, puis il faut payer. Ce n'est pas une offre gratuite, c'est une démo.",
          ],
        },
        { type: ArticleBlockType.H2, text: "Les outils avec une vraie offre gratuite" },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Le plus généreux en usage individuel",
          desc: "Enregistrements et transcriptions sans restriction de volume ni de durée sur l'usage individuel. Comptes rendus automatiques inclus. C'est aujourd'hui la référence si tu es seul et que tu veux arrêter de prendre des notes à la main.",
          limit:
            "la générosité porte sur la capture. L'analyse commerciale de fond, ce qui revient d'un appel à l'autre, reste hors de portée.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "Généreux, avec des crédits",
          desc: "Réunions et transcriptions illimitées sur le palier gratuit, plus des extraits vidéo partageables très pratiques pour faire circuler un moment d'appel dans l'équipe.",
          limit:
            "les fonctions IA fonctionnent avec des crédits, et le stockage est réduit sur le gratuit. Tu enregistres sans compter, tu analyses en comptant.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fireflies",
          tag: "Limites de transcription plus larges",
          desc: "Palier gratuit avec des quotas de transcription rapportés comme plus larges que ceux de certains concurrents, et un bon niveau d'intégrations.",
          limit:
            "l'interface et les fonctions avancées poussent assez vite vers les plans payants dès qu'on travaille en équipe.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT ou Claude",
          tag: "L'option que personne ne cite",
          desc: "Coller une transcription dans un assistant généraliste donne souvent un excellent résultat sur un appel isolé, pour zéro euro si tu as déjà un compte.",
          limit:
            "il repart de zéro à chaque fois. Aucune mémoire d'un appel à l'autre, et tu réexpliques ton contexte à chaque prompt. Excellent pour un appel, ingérable pour cinquante.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "À l'inverse, **Leexi n'a pas d'offre gratuite**, et Modjo se vend sur devis avec un engagement annuel. Ce ne sont pas des options si ton budget est à zéro.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Outil", "Ce que couvre le gratuit"],
          rows: [
            ["Fathom", "Enregistrement et transcription illimités en usage individuel."],
            ["tl;dv", "Réunions et transcriptions illimitées, fonctions IA sur crédits."],
            ["Fireflies", "Quotas de transcription larges, intégrations correctes."],
            ["ChatGPT ou Claude", "Illimité de fait, mais sans mémoire entre les appels."],
            ["Leexi", "Rien. Pas de palier gratuit."],
            ["Modjo", "Rien. Devis et engagement annuel."],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ces paliers bougent souvent, la gratuité étant un argument de conquête. Vérifie chez l'éditeur avant de t'engager sur un usage.",
        },
        { type: ArticleBlockType.H2, text: "Le plafond que le gratuit ne franchit jamais" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et il ne s'agit pas d'un quota. Tous ces outils, gratuits comme payants, répondent à la même question : *qu'est-ce qui s'est dit pendant cet appel ?* Aucun ne répond à la suivante, qui est celle qui change ton quotidien : *qu'est-ce que j'en fais ?*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tu accumules trente comptes rendus impeccables et gratuits. Pendant ce temps, tu réécris tes messages de prospection à l'intuition, dans un autre onglet, sans jamais rouvrir ces trente fichiers.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce que le gratuit te donne",
            items: [
              "Une transcription propre de chaque appel",
              "Un résumé que tu ne reliras probablement pas",
              "Des extraits à partager",
              "Un dossier qui grossit",
            ],
          },
          good: {
            title: "Ce qui te manque encore",
            items: [
              "Ce qui revient d'un appel à l'autre",
              "Le portrait de qui achète vraiment chez toi",
              "Des critères de ciblage tirés de ces signaux",
              "Des messages écrits avec les mots de tes clients",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly commence là, et se teste aussi gratuitement",
          text: "Meidly ne remplace pas Fathom ni tl;dv, il ne cherche pas à enregistrer tes réunions mieux qu'eux. Il part de ce qui se dit dans tes échanges pour construire ton avatar client, en tirer des critères de ciblage, puis des messages. Tu peux l'essayer avec des crédits offerts, sans carte bancaire, sur tes propres appels.",
          linkLabel: "Voir l'analyse des appels de Meidly",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "Par quoi commencer concrètement" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu ne captures rien aujourd'hui, prends **Fathom**. C'est gratuit, c'est immédiat, et tu auras enfin une trace de tes conversations.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu as déjà tes transcriptions et que tu veux comprendre ce qu'elles contiennent, un assistant généraliste te dépannera sur quelques appels. Au-delà, le fait de tout réexpliquer à chaque fois devient le vrai coût.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et si ta question n'est plus *comment enregistrer* mais **que faire de ce qui a été dit**, aucun palier gratuit de notetaker ne t'y amènera, parce que ce n'est pas leur métier.",
        },
      ],
      faq: [
        {
          question: "Quel est le meilleur outil gratuit pour analyser ses appels de vente ?",
          answer:
            "Fathom est aujourd'hui le plus généreux en usage individuel, avec enregistrement et transcription sans limite de volume. tl;dv est très proche, avec des réunions illimitées mais des fonctions IA comptées en crédits. Fireflies propose des quotas de transcription larges. Les trois transcrivent et résument très bien, aucun ne fait d'analyse commerciale transversale.",
        },
        {
          question: "Peut-on analyser ses appels avec ChatGPT gratuitement ?",
          answer:
            "Oui. Coller une transcription dans ChatGPT ou Claude donne souvent un très bon résultat sur un appel isolé, sans rien payer si tu as déjà un compte. La limite apparaît avec le volume : l'assistant repart de zéro à chaque conversation, ne garde aucun contexte d'un appel au suivant, et t'oblige à tout réexpliquer à chaque fois.",
        },
        {
          question: "Leexi ou Modjo ont-ils une version gratuite ?",
          answer:
            "Non, ni l'un ni l'autre. Leexi publie une grille tarifaire mais sans palier gratuit, et Modjo se vend sur devis avec un engagement annuel et un nombre minimum de sièges. Pour tester sans budget, il faut se tourner vers les notetakers.",
        },
        {
          question: "Un outil gratuit suffit-il pour mieux vendre ?",
          answer:
            "Pour ne rien oublier d'une réunion, oui, largement. Pour vendre mieux, non, et ce n'est pas une question de version payante : même les outils les plus chers s'arrêtent à l'analyse. Ce qui change quelque chose, c'est de réutiliser ces signaux dans ton ciblage et tes messages, et c'est une autre catégorie de produit.",
        },
      ],
    },
    en: {
      title: "Analysing sales calls for free: what is actually possible",
      excerpt:
        "Several tools record and transcribe without charging you, and they are very good. Here is which ones, what their free tier really covers, and the ceiling none of them passes.",
      description:
        "Which tools let you analyse your sales calls for free, and how far does their free tier really go? A comparison of the free plans from Fathom, tl;dv and Fireflies, and the limit they all share.",
      ctaTitle: "Try what comes after the transcript",
      ctaText:
        "Meidly gives you credits to analyse your own calls and see what happens when the data feeds your targeting. No credit card.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Yes, you can analyse your calls without paying. And no, it is not a crippled version that pushes you to pay after three meetings.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The notetaker market got so crowded that free became a weapon. Several tools now offer a free tier that is genuinely usable day to day, not a disguised demo.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You still need to know what each one covers, and above all where free stops in every case.",
        },
        { type: ArticleBlockType.H2, text: "What free means here" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Three different things hide behind the word, which is what makes comparisons unreadable:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Free on volume**: unlimited meetings and transcription time. That is real free.",
            "**Free with AI credits**: you record as much as you want, but summaries and questions to the AI are counted.",
            "**Free as a trial**: a few days or a few hours, then you pay. That is not a free plan, it is a demo.",
          ],
        },
        { type: ArticleBlockType.H2, text: "The tools with a genuine free plan" },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Most generous for individual use",
          desc: "Recordings and transcriptions with no volume or duration limit on individual use. Automatic summaries included. It is currently the reference if you work alone and want to stop taking notes by hand.",
          limit:
            "the generosity is about capture. Deep commercial analysis, what comes back from one call to the next, stays out of reach.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "Generous, with credits",
          desc: "Unlimited meetings and transcription on the free tier, plus shareable video clips that are very handy to circulate a moment of a call inside the team.",
          limit:
            "AI features run on credits and storage is reduced on the free plan. You record without counting, you analyse while counting.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fireflies",
          tag: "Wider transcription limits",
          desc: "A free tier with transcription quotas reported as wider than some competitors, and a good level of integrations.",
          limit:
            "the interface and the advanced features push fairly quickly toward paid plans as soon as you work as a team.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT or Claude",
          tag: "The option nobody lists",
          desc: "Pasting a transcript into a general purpose assistant often gives an excellent result on a single call, for nothing if you already have an account.",
          limit:
            "it starts from scratch every time. No memory from one call to the next, and you re explain your context on every prompt. Excellent for one call, unmanageable for fifty.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "On the other side, **Leexi has no free plan**, and Modjo sells on quote with an annual commitment. Neither is an option when your budget is zero.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Tool", "What the free tier covers"],
          rows: [
            ["Fathom", "Unlimited recording and transcription for individual use."],
            ["tl;dv", "Unlimited meetings and transcription, AI features on credits."],
            ["Fireflies", "Wide transcription quotas, decent integrations."],
            ["ChatGPT or Claude", "Effectively unlimited, but with no memory between calls."],
            ["Leexi", "Nothing. No free tier."],
            ["Modjo", "Nothing. Quote and annual commitment."],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "These tiers move often, since free is a customer acquisition argument. Check with the vendor before committing to a workflow.",
        },
        { type: ArticleBlockType.H2, text: "The ceiling free never passes" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And it is not a quota. All these tools, free and paid alike, answer the same question: *what was said during this call?* None answers the next one, which is the one that changes your day to day: *what do I do with it?*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You pile up thirty flawless free summaries. Meanwhile you rewrite your prospecting messages from intuition, in another tab, without ever reopening those thirty files.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What free gives you",
            items: [
              "A clean transcript of every call",
              "A summary you probably will not reread",
              "Clips to share",
              "A folder that grows",
            ],
          },
          good: {
            title: "What you are still missing",
            items: [
              "What comes back from one call to the next",
              "The portrait of who actually buys from you",
              "Targeting criteria drawn from those signals",
              "Messages written with your customers own words",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly starts there, and is also free to try",
          text: "Meidly does not replace Fathom or tl;dv, and does not try to record your meetings better than they do. It starts from what is said in your conversations to build your client avatar, turn it into targeting criteria, then into messages. You can try it with free credits, no credit card, on your own calls.",
          linkLabel: "See Meidly call analysis",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "Where to actually start" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you capture nothing today, take **Fathom**. It is free, immediate, and you will finally have a record of your conversations.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you already have transcripts and want to understand what is in them, a general purpose assistant will do for a handful of calls. Beyond that, re explaining everything each time becomes the real cost.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And if your question is no longer *how do I record* but **what do I do with what was said**, no notetaker free tier will take you there, because that is not their job.",
        },
      ],
      faq: [
        {
          question: "What is the best free tool to analyse sales calls?",
          answer:
            "Fathom is currently the most generous for individual use, with recording and transcription and no volume limit. tl;dv is very close, with unlimited meetings but AI features counted in credits. Fireflies offers wide transcription quotas. All three transcribe and summarise very well, and none of them does cross call commercial analysis.",
        },
        {
          question: "Can you analyse your calls with ChatGPT for free?",
          answer:
            "Yes. Pasting a transcript into ChatGPT or Claude often gives a very good result on a single call, for nothing if you already have an account. The limit shows with volume: the assistant starts from scratch on every conversation, keeps no context from one call to the next, and forces you to re explain everything each time.",
        },
        {
          question: "Do Leexi or Modjo have a free version?",
          answer:
            "Neither does. Leexi publishes a pricing grid but with no free tier, and Modjo sells on quote with an annual commitment and a minimum number of seats. To test without a budget, you have to turn to the notetakers.",
        },
        {
          question: "Is a free tool enough to sell better?",
          answer:
            "To forget nothing from a meeting, yes, easily. To sell better, no, and it is not a matter of paid versions: even the most expensive tools stop at analysis. What changes something is reusing those signals in your targeting and your messages, and that is a different category of product.",
        },
      ],
    },
  },
};
